import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import React from "react";

/**
 * FormPageTemplate - Template for form-based pages
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle/description
 * @param {string} props.error - Error message to display
 * @param {string} props.success - Success message to display
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onCancel - Cancel button handler
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {string} props.submitButtonText - Text for submit button
 * @param {string} props.cancelButtonText - Text for cancel button
 * @param {React.ReactNode} props.children - Form content
 */
const FormPageTemplate = ({
  title,
  subtitle,
  error,
  success,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitButtonText = "Submit",
  cancelButtonText = "Cancel",
  children,
}) => {
  return (
    <div className="min-h-screen bg-[#0f1418] p-6">
      <div className="max-w-3xl mx-auto bg-[#1a1e21] rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-100">{title}</h1>
          {subtitle && <p className="text-gray-400">{subtitle}</p>}
        </div>

        {error && (
          <sl-alert variant="danger" open className="mb-4">
            <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
            <strong>Error:</strong> {error}
          </sl-alert>
        )}

        {success && (
          <sl-alert variant="success" open className="mb-4">
            <sl-icon slot="icon" name="check2-circle"></sl-icon>
            <strong>Success:</strong> {success}
          </sl-alert>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          {children}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-300 bg-[#262b30] rounded hover:bg-[#2f3236] border border-[#2f3236]"
              disabled={isSubmitting}
            >
              {cancelButtonText}
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-sm text-white bg-[#4db1e0] rounded hover:bg-[#3da0cf] focus:outline-none focus:ring-2 focus:ring-[#4db1e0] ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormPageTemplate;
